import * as React from 'react';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from 'components/ui/command';
import { FormControl, FormItem, FormMessage } from 'components/ui/form';
import { Label } from 'components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from 'components/ui/popover';
import { cn } from 'lib/utils';
import { SelectContentProps } from './selectGs';

interface SearchableMultiSelectGsProps {
  label?: string;
  content: SelectContentProps[];
  isLoading?: boolean;
  disabled?: boolean;
  value?: string[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  onValueChange?: (value: string[]) => void;
}

const normalizeText = (text: string) =>
  text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

const SearchableMultiSelectGs = React.forwardRef<
  HTMLDivElement,
  SearchableMultiSelectGsProps
>(
  (
    {
      label,
      content,
      isLoading = false,
      disabled,
      value = [],
      placeholder = 'Selecione',
      searchPlaceholder = 'Buscar',
      emptyMessage = 'Nenhum resultado',
      onValueChange,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false);

    const selectedValues = React.useMemo(
      () => value.map(String),
      [value]
    );

    const selectedItems = React.useMemo(
      () =>
        content.filter((item) =>
          selectedValues.includes(String(item.value))
        ),
      [content, selectedValues]
    );

    const sortedContent = React.useMemo(() => {
      return [...content].sort((a, b) => {
        const aSelected = selectedValues.includes(String(a.value));
        const bSelected = selectedValues.includes(String(b.value));

        if (aSelected === bSelected) return 0;

        return aSelected ? -1 : 1;
      });
    }, [content, selectedValues]);

    const visibleSelectedItems = selectedItems.slice(0, 3);

    const remainingSelectedCount = Math.max(
      selectedItems.length - visibleSelectedItems.length,
      0
    );

    return (
      <FormItem ref={ref}>
        <div className="grid w-full items-center gap-1.5">
          {label && (
            <Label className="font-GilroySemibold text-sm text-slate-800">
              {label}
            </Label>
          )}

          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <FormControl>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={open}
                  disabled={isLoading || disabled}
                  className={cn(
                    'h-10 w-full max-w-full overflow-hidden justify-between border-solid text-left font-sans text-slate-900 focus-visible:ring-primary-500',
                    selectedItems.length === 0 && 'text-muted-foreground'
                  )}
                >
                  <div className="flex min-w-0 flex-1 items-center gap-1 overflow-hidden">
                    {visibleSelectedItems.length ? (
                      <>
                        {visibleSelectedItems.map((item) => (
                          <span
                            key={String(item.value)}
                            className="flex max-w-[180px] shrink-0 items-center gap-1 rounded border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                          >
                            <span className="truncate">
                              {item.name}
                            </span>

                            <button
                              type="button"
                              className="shrink-0 rounded-sm hover:bg-slate-200"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();

                                const newValue = selectedValues.filter(
                                  (value) => value !== String(item.value)
                                );

                                onValueChange?.(newValue);
                              }}
                            >
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}

                        {remainingSelectedCount > 0 && (
                          <span className="shrink-0 rounded border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-GilroySemibold text-slate-700">
                            +{remainingSelectedCount}
                          </span>
                        )}
                      </>
                    ) : (
                      <span className="truncate text-xs text-muted-foreground">
                        {placeholder}
                      </span>
                    )}
                  </div>

                  {isLoading ? (
                    <svg
                      aria-hidden="true"
                      className="ml-2 h-4 w-4 shrink-0 animate-spin fill-blue-600 text-gray-200"
                      viewBox="0 0 100 101"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                        fill="currentColor"
                      />
                      <path
                        d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                        fill="currentFill"
                      />
                    </svg>
                  ) : (
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  )}
                </Button>
              </FormControl>
            </PopoverTrigger>

            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
              <Command
                filter={(commandValue, search) => {
                  if (!search) return 1;

                  return normalizeText(commandValue).includes(
                    normalizeText(search)
                  )
                    ? 1
                    : 0;
                }}
              >
                <CommandInput placeholder={searchPlaceholder} />

                <CommandList>
                  <CommandEmpty>{emptyMessage}</CommandEmpty>

                  <CommandGroup>
                    {sortedContent.map((item) => {
                      const itemValue = String(item.value);
                      const isSelected =
                        selectedValues.includes(itemValue);

                      return (
                        <CommandItem
                          key={itemValue}
                          value={`${item.name} ${itemValue}`}
                          onSelect={() => {
                            const newValue = isSelected
                              ? selectedValues.filter(
                                  (value) => value !== itemValue
                                )
                              : [...selectedValues, itemValue];

                            onValueChange?.(newValue);
                          }}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              isSelected
                                ? 'opacity-100'
                                : 'opacity-0'
                            )}
                          />

                          <span className="truncate">
                            {item.name}
                          </span>
                        </CommandItem>
                      );
                    })}
                  </CommandGroup>
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          <FormMessage />
        </div>
      </FormItem>
    );
  }
);

SearchableMultiSelectGs.displayName = 'SearchableMultiSelectGs';

export { SearchableMultiSelectGs };